import React, { useEffect } from 'react';
import { FiChevronLeft } from 'react-icons/fi';

import { AdSlot } from '../GoogleAds/AdSlot';
import { MobileNav } from '../Navbar';

import { Button, Container, NavBar } from '@/components';
import useAuth from '@/hooks/useAuth';
import { useGoogleAds } from '@/hooks/useGoogleAds';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePermutive } from '@/hooks/usePermutive';
import { PropsWithClassName } from '@/types';
import { AdsSections } from '@/types/ads';
import { cn, letterGenderById } from '@/utils';
import { ADS_DIV_ID, SECTION_NAME_FOR_ADS } from '@/utils/ads';

interface Props extends MobileNav, PropsWithClassName {
  coverTop?: () => React.ReactNode;
  main: () => React.ReactNode;
  sidebar?: () => React.ReactNode;
  extraSuperMegaImportantButton?: () => React.ReactNode;
  sidebarClassName?: string;
  footer?: () => React.ReactNode;
  tabs?: () => React.ReactNode;
  actionFooter?: {
    buttonName: string;
    actionHandler: () => void;
    buttonDisabled?: boolean;
  };
  footerContent?: () => React.ReactNode;
  withBanners?: boolean;
  backButton?: boolean;
  backButtonAction?: () => void;
  adsSections?: AdsSections;
  trackProps?: {
    info?: boolean;
    escuela?: string;
    fechaOro?: boolean;
    medallas?: number;
    primaria?: boolean;
    secundario?: boolean;
    torneoAmigosPremios?: boolean;
  };
}

export const Layout = React.memo(
  ({
    main,
    sidebar,
    coverTop,
    actionFooter,
    footerContent,
    tabs,
    withBanners = true,
    className,
    sidebarClassName,
    mobile,
    backButton,
    extraSuperMegaImportantButton,
    footer,
    backButtonAction,
    adsSections = SECTION_NAME_FOR_ADS.torneodeamigos,
    trackProps,
  }: Props) => {
    const { isMobile, isDesktop } = useIsDesktop();
    useGoogleAds({ adsSections });

    const { userInitialData: userData } = useAuth();
    const { trackPageView, isReady: isPermutiveReady } = usePermutive();

    useEffect(() => {
      // Permutive esta cargado
      if (isPermutiveReady) {
        trackPageView({
          section: adsSections,
          kw_premium: userData?.premium,
          kw_edad: userData?.edad_dt,
          kw_sexo: letterGenderById(userData?.idSexo),
          kw_equipo: userData?.club_hincha,
          kw_fecha: userData?.fecha,
          kw_pines: userData?.pinesDisponibles,
          kw_saldo: userData?.saldo,
          kw_puntosporfecha: userData?.puntos_fecha,
          kw_puntosportorneo: userData?.puntos_general,
          kw_posicionporfecha: userData?.posicion_fecha,
          kw_posicionportorneo: userData?.posicion_general,
          kw_recibirinfo: userData?.recibeInformacion,
          ...(trackProps && {
            kw_recibirinfo: trackProps.info || false,
            kw_escuela: trackProps.escuela,
            kw_fechaoro: trackProps.fechaOro,
            kw_medallas: trackProps.medallas,
            kw_primaria: trackProps.primaria,
            kw_secundario: trackProps.secundario,
            kw_torneoamigospremios: trackProps.torneoAmigosPremios,
          }),
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPermutiveReady]);

    return (
      <NavBar desktop heightAuto="auto" mobile={mobile}>
        <Container
          className={cn(`relative lg:px-0 lg:mt-0 ${className}`, {
            'pb-[116px]':
              (actionFooter && isMobile) || (footerContent && isMobile),
          })}
          variant="default-1"
        >
          <div className="flex flex-col">
            {withBanners && (
              <AdSlot
                className={cn('order-2 lg:order-1 lg:mt-5', {
                  'order-3': extraSuperMegaImportantButton,
                })}
                divId={ADS_DIV_ID.horizontal}
                variant="HORIZONTAL"
              />
            )}
            {extraSuperMegaImportantButton && (
              <div className="order-2 text-center">
                {extraSuperMegaImportantButton()}
              </div>
            )}
            {(tabs || backButton) && (
              <div className="relative lg:order-2 order-1">
                {backButton && (
                  <Button
                    className="text-gdt-blue font-bold relative left-0 top-16 no-underline"
                    type="button"
                    variant="text"
                    onClick={() =>
                      backButtonAction ? backButtonAction() : history.back()
                    }
                  >
                    <FiChevronLeft className="text-gdt-blue w-6 h-6 inline" />
                    <span className="pl-[4px]">Volver</span>
                  </Button>
                )}
                <div className="mt-1 mb-5 lg:mt-7 lg:mb-9 justify-center flex">
                  {tabs && tabs()}
                </div>
              </div>
            )}
          </div>
          {coverTop && <header className="w-full">{coverTop()}</header>}
          <div
            className={cn('flex flex-col lg:flex-row w-full', {
              'lg:mt-8': !tabs,
            })}
          >
            <section className="flex-1">{main()}</section>
            <aside className={sidebarClassName}>
              {sidebar ? sidebar() : <></>}
            </aside>
          </div>
          {footer && footer()}
          {withBanners && (
            <div className="justify-center flex lg:space-x-[99px] lg:mb-12 lg:mt-14 my-6">
              <AdSlot divId={ADS_DIV_ID.caja1} variant="BOX" />
              {isDesktop && (
                <>
                  <AdSlot divId={ADS_DIV_ID.caja2} variant="BOX" />
                  <AdSlot divId={ADS_DIV_ID.caja3} variant="BOX" />
                </>
              )}
            </div>
          )}
        </Container>
        {actionFooter && isMobile && (
          <div className="bg-white px-4 pt-5 pb-8 fixed bottom-0 w-full shadow-[0_0_10px_rgba(0,0,0,0.1)]">
            <Button
              className="w-full"
              disabled={actionFooter.buttonDisabled}
              type="button"
              variant="primary"
              onClick={actionFooter.actionHandler}
            >
              {actionFooter.buttonName}
            </Button>
          </div>
        )}
        {footerContent && isMobile && (
          <div className="bg-white px-4 pt-5 pb-8 fixed bottom-0 w-full shadow-[0_0_10px_rgba(0,0,0,0.1)]">
            {footerContent()}
          </div>
        )}
      </NavBar>
    );
  }
);

Layout.displayName = 'Layout';
